import React from "react";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { MAIN_THEME_COLOR, SECONDARY_THEME_COLOR } from "../../../providers/theme/colors/colors";

type DycSubmissionSuccessModalProps = {
  open: boolean;
  onClose: () => void;
  fileName: string;
  blobUrl: string;
  onDownload: () => void;
  parishName?: string;
  recipientEmail?: string;
  isPitchBattle?: boolean;
};

export default function DycSubmissionSuccessModal({
  open,
  onClose,
  fileName,
  blobUrl,
  onDownload,
  parishName,
  recipientEmail,
  isPitchBattle,
}: DycSubmissionSuccessModalProps) {
  const handleViewPdf = () => {
    if (blobUrl) {
      window.open(blobUrl, "_blank");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          p: { xs: 1, sm: 2 },
        },
      }}
    >
      <DialogTitle sx={{ pt: 2, pb: 1, textAlign: "center" }}>
        <Stack alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              backgroundColor: "#e8f5e9",
              color: "#2e7d32",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 14px rgba(46, 125, 50, 0.2)",
            }}
          >
            <CheckCircleOutlineRoundedIcon sx={{ fontSize: 44 }} />
          </Box>
          <Typography variant="h5" fontWeight={800} color={MAIN_THEME_COLOR}>
            Registration Submitted Successfully!
          </Typography>
          <Typography variant="body2" color="text.secondary" maxWidth={540} textAlign="center">
            {isPitchBattle
              ? `The Pitch Battle registration for ${parishName || "your unit"} has been recorded.`
              : `The DYC Yuva Dabazo 2026 registration for ${parishName || "your unit"} has been recorded.`}
            <br />
            Your registration PDF receipt has been automatically downloaded to your device.
          </Typography>
          <Box
            sx={{
              backgroundColor: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: 2,
              px: 2,
              py: 0.75,
              mt: 0.5,
            }}
          >
            <Typography variant="caption" color="#166534" fontWeight={600} align="center" display="block">
              📧 A copy has been automatically emailed to {recipientEmail || "your email"} and the organizers.
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        <Stack spacing={2}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 3,
              backgroundColor: "#f8fafc",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color={MAIN_THEME_COLOR}>
                Registration PDF Receipt
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                File: {fileName}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={handleViewPdf}
                sx={{ borderRadius: 999, px: 2.5 }}
              >
                View PDF
              </Button>

              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={onDownload}
                sx={{
                  borderRadius: 999,
                  px: 2.5,
                  backgroundColor: MAIN_THEME_COLOR,
                  "&:hover": { backgroundColor: MAIN_THEME_COLOR },
                }}
              >
                Download PDF Again
              </Button>
            </Stack>
          </Paper>

          {/* Embedded PDF Preview */}
          {blobUrl ? (
            <Box
              sx={{
                width: "100%",
                height: 400,
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid #e2e8f0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              <iframe
                src={blobUrl}
                title="Registration Receipt PDF Preview"
                width="100%"
                height="100%"
                style={{ border: "none" }}
              />
            </Box>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: "center" }}>
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{
            borderRadius: 999,
            px: 5,
            background: `linear-gradient(90deg, ${MAIN_THEME_COLOR} 0%, ${SECONDARY_THEME_COLOR} 100%)`,
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
